import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import AddToWishListButton from '../components/AddToWishListButton/AddToWishListButton';
import ErrorMessage from '../components/Errors/ErrorMessage/ErrorMessage';
import WishListList from '../components/WishListList/WishListList';
import styles from '../App.module.scss';
import { ViewWishListButton } from '../components/ViewMyWishListButton/ViewMyWishListButton';
import Cookies from 'js-cookie';
import { getExperiencesFromWindow, isFlagSet } from '../helpers/featureFlags/featureFlags';
import { getExperiences } from '../helpers/productDetails/productDetails';
import { fetchMonetateExperiences } from '../helpers/monetate/monetate';
import { FEATURE_FLAGS } from '../helpers/featureFlags/constants';
import { navigate, addQueryParameterToURL, getUrl } from '../helpers/redirect/redirect';

interface ContainerProps {
    handleWishListOnClick: (wishListId: string) => Promise<void> | never;
    productExistsInWishLists?: boolean;
    shouldWishListsBeExpanded: boolean;
    hasError: boolean;
    hasMoreThanOneWishlist: boolean;
    wishLists?: ProductWishList[];
    errorMessage: string;
    isCallingAPI: boolean;
    productId: string;
}

const fireNewRelicEventForMyJLLink = (): void => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window.newrelic.addPageAction !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.newrelic.addPageAction('wishlist-join-myjl-link-click');
    }
};

const handleSignInClick = (baseUrl: string): void => {
    const continueToURL = getUrl();
    const continueToURLWithQueryParams = addQueryParameterToURL(continueToURL, 'wishlist', 'interacted');
    const locationURL = `${baseUrl}?continueTo=${window.encodeURIComponent(continueToURLWithQueryParams)}`;
    navigate(locationURL);
};

const isGuestWishlistEnabled = async (): Promise<boolean> => {
    const isGuest = !Cookies.get('shopperId');
    const windowExperiences = getExperiencesFromWindow();
    const experiences = getExperiences();
    const headerExperiences =
        windowExperiences.length === 0 && experiences.length === 0 ? await fetchMonetateExperiences() : [];
    return isGuest && isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST, experiences || headerExperiences);
};

const Container: React.FC<ContainerProps> = observer(
    ({
        handleWishListOnClick,
        productExistsInWishLists,
        shouldWishListsBeExpanded = false,
        wishLists,
        isCallingAPI = false,
        hasError = false,
        errorMessage = '',
        hasMoreThanOneWishlist = false,
        productId,
    }) => {
        const [guestWishlistEnabled, setGuestWishlistEnabled] = useState<boolean>(false);

        useEffect(() => {
            isGuestWishlistEnabled().then((result) => {
                setGuestWishlistEnabled(result);
            });
        }, []);

        const signInLink = '/sign-in';
        const registerLink = '/register';
        const signInLinkUrl = `${process.env.REACT_APP_BASE_REDIRECT_URL}${signInLink}`;
        const registerLinkUrl = `${process.env.REACT_APP_BASE_REDIRECT_URL}${registerLink}`;
        return (
            <>
                <AddToWishListButton
                    handleWishListOnClick={handleWishListOnClick}
                    isAdded={productExistsInWishLists}
                    isExpanded={shouldWishListsBeExpanded}
                    isSubmitting={isCallingAPI}
                    productId={productId}
                />
                <ErrorMessage hasError={hasError} message={errorMessage} />
                <WishListList
                    wishLists={wishLists}
                    handleWishListOnClick={handleWishListOnClick}
                    isExpanded={shouldWishListsBeExpanded}
                    isSubmitting={isCallingAPI}
                    productId={productId}
                />
                {shouldWishListsBeExpanded && !guestWishlistEnabled && (
                    <div className={styles.viewMyWishListButtonContainer}>
                        <div className={styles.wishlistMyjl}>
                            <div>Want the chance to win your wish list each month?</div>
                            <a
                                className="wishlist-join-myjl-link"
                                onClick={fireNewRelicEventForMyJLLink}
                                href="https://account.johnlewis.com/rewards#intcmp=ic_20221013_winyourwishlistpdptest_pp_gif_b_insb_"
                            >
                                {' '}
                                Enter with My John Lewis{' '}
                            </a>
                        </div>
                        <ViewWishListButton hasMoreThanOneList={hasMoreThanOneWishlist} />
                    </div>
                )}
                {shouldWishListsBeExpanded && guestWishlistEnabled && (
                    <div className={styles.viewMyWishListButtonContainer}>
                        <div className={styles.guestWishlistMyjl}>
                            <div>
                                We&rsquo;ll keep your wish list for 60 days.{' '}
                                <a href="#" onClick={(): void => handleSignInClick(signInLinkUrl)}>
                                    Sign in
                                </a>{' '}
                                or{' '}
                                <a href="#" onClick={(): void => handleSignInClick(registerLinkUrl)}>
                                    register
                                </a>{' '}
                                to save your items across your devices.
                            </div>
                        </div>
                        <ViewWishListButton hasMoreThanOneList={hasMoreThanOneWishlist} />
                    </div>
                )}
            </>
        );
    },
);

export default Container;
